import styled, { css } from "styled-components";

import {
  weight,
  type,
  tracking,
  radius,
  motion,
  media,
  font,
  color,
} from "../../../styles/tokens";

/* One button, three variants. Replaces nine bespoke CTA components across the marketing pages
 * (Hero x2, Header x2, Rooms, BookingWidget, room_types, identity, plus a dead one in Footer),
 * each with its own padding, radius, transition and hover treatment.
 *
 * Renders as <button> by default; pass `as={Link}` or `as="a"` for navigation. A button that
 * navigates should be an anchor so it opens in a new tab, shows a status-bar URL, and is announced
 * as a link — several of the originals were <button onClick={scrollTo}> where an href belonged.
 */

const variants = {
  primary: css`
    background: ${color.primary};
    color: ${color.onDark};
    border-color: ${color.primary};

    &:hover {
      background: ${color.primaryHover};
      border-color: ${color.primaryHover};
    }
  `,
  secondary: css`
    background: transparent;
    color: ${color.ink};
    border-color: ${color.borderStrong};

    &:hover {
      border-color: ${color.ink};
    }
  `,
  /* On photography. Solid enough to stay legible over a moving video frame. */
  onDark: css`
    background: rgba(246, 244, 238, 0.12);
    color: ${color.onDark};
    border-color: rgba(246, 244, 238, 0.5);
    backdrop-filter: blur(6px);

    &:hover {
      background: rgba(246, 244, 238, 0.22);
      border-color: ${color.onDark};
    }
  `,
};

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: ${(props) => (props.$size === "lg" ? "16px 32px" : "12px 24px")};
  border: 1px solid transparent;
  border-radius: ${radius.sm};
  font-family: ${font.body};
  font-size: ${(props) => (props.$size === "lg" ? type.base : type.sm)};
  font-weight: ${weight.semibold};
  letter-spacing: ${tracking.wide};
  text-transform: uppercase;
  cursor: pointer;
  transition:
    background ${motion.base},
    border-color ${motion.base},
    color ${motion.base};

  ${(props) => variants[props.$variant || "primary"]}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${media.sm`
    width: 100%;
  `}
`;

export default Button;
